import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Link, ArrowRight, Plus, Trash2, Eye, Share2, Clock, CheckCircle, User, Calendar } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { PageLayout } from '@/components/layout/PageLayout';
import { api } from '../services/client';

interface Attribution {
  id: string;
  source: string;
  url: string;
  dateAccessed: Date;
  usedIn: string;
  context: string;
  verified: boolean;
}

const AttributionTracker: React.FC = () => {
  const [selectedAttribution, setSelectedAttribution] = useState<Attribution | null>(null);
  const [attributions, setAttributions] = useState<Attribution[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/citations/provenance/list')
      .then(r => {
        const data = r.data?.entries ?? r.data ?? [];
        // Normalize: backend may return objects, we want Attribution[].
        const list = Array.isArray(data) ? data : [];
        // Guard against partial shapes (e.g. server may send {id, ...}
        // but no `source`/`url`/`verified`). The page's render path
        // calls methods on these; missing fields would throw on
        // .toLocaleDateString() etc.
        const normalized: Attribution[] = list
          .filter((a: any) => a && typeof a === 'object')
          .map((a: any) => ({
            id: String(a.id ?? crypto.randomUUID()),
            source: String(a.source ?? a.title ?? 'Unknown source'),
            url: String(a.url ?? ''),
            dateAccessed: a.dateAccessed instanceof Date ? a.dateAccessed : new Date(a.dateAccessed ?? Date.now()),
            usedIn: String(a.usedIn ?? a.used_in ?? ''),
            context: String(a.context ?? ''),
            verified: Boolean(a.verified),
          }));
        setAttributions(normalized);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <PageLayout
      title="Attribution Tracker"
      description="Track and manage content attribution and sources"
      icon={FileText}
    >

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Attributions', value: attributions.length, textClass: 'text-khiimori-accent-violet' },
          { label: 'Verified', value: attributions.filter(a => a.verified).length, textClass: 'text-success-500' },
          { label: 'Pending', value: attributions.filter(a => !a.verified).length, textClass: 'text-warning-500' },
          { label: 'This Week', value: attributions.filter(a => (Date.now() - a.dateAccessed.getTime()) < 604800000).length, textClass: 'text-khiimori-accent-fuchsia' },
        ].map((stat, index) => (
          <motion.div key={index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
            <Card>
              <div className="text-center">
                <div className={`text-3xl font-bold ${stat.textClass}`}>
                  {stat.value}
                </div>
                <div className="text-sm text-khiimori-text-secondary">{stat.label}</div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {!loading && attributions.length === 0 && (
        <p className="text-sm text-khiimori-text-muted py-8 text-center">No attributions yet. Citations you add will appear here as provenance records.</p>
      )}
      {loading && (
        <div className="text-center text-khiimori-text-muted py-8">Loading attributions...</div>
      )}
      <div className="space-y-4">
        {attributions.map((attr, index) => (
          <motion.div key={attr.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
            <Card className="cursor-pointer" onClick={() => setSelectedAttribution(attr)}>
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <div className="p-3 bg-khiimori-accent-violet rounded-xl">
                    <FileText className="h-6 w-6 text-khiimori-text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-bold text-khiimori-text-primary">{attr.source}</h3>
                      {attr.verified && (
                        <span className="flex items-center space-x-1 px-2 py-0.5 bg-success-500/20 text-success-500 rounded-full text-xs">
                          <CheckCircle className="h-3 w-3" /> Verified
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-primary-500 dark:text-primary-400 mb-2">{attr.url}</p>
                    <div className="flex items-center space-x-4 text-sm text-khiimori-text-secondary">
                      <span className="flex items-center"><Link className="h-4 w-4 mr-1" /> Used in: {attr.usedIn}</span>
                      <span className="flex items-center"><Clock className="h-4 w-4 mr-1" /> {attr.dateAccessed.toLocaleDateString()}</span>
                    </div>
                    <p className="mt-2 text-sm text-khiimori-text-secondary italic">"{attr.context}"</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button className="p-2 hover:bg-khiimori-bg-elevated rounded-lg"><Eye className="h-5 w-5 text-khiimori-text-secondary" /></button>
                  <button className="p-2 hover:bg-khiimori-bg-elevated rounded-lg"><Share2 className="h-5 w-5 text-primary-500" /></button>
                  <button className="p-2 hover:bg-khiimori-bg-elevated rounded-lg"><Trash2 className="h-5 w-5 text-error-500" /></button>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>
    </PageLayout>
  );
};

export default AttributionTracker;